import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Alert,
  Chip,
  Tooltip
} from '@mui/material';
import {
  AccountBalance as CurrencyIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { getCurrencyOptions, DEFAULT_CURRENCY } from '../../utils/currency';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const Settings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    currency: {
      display: DEFAULT_CURRENCY,
      autoConvert: true
    }
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/users/settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.settings) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      await axios.put('/api/users/settings', { settings }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSettings({
      currency: {
        display: DEFAULT_CURRENCY,
        autoConvert: true
      }
    });
    setSuccess(false);
    setError('');
  };

  const updateCurrencySetting = (field, value) => {
    setSettings(prev => ({
      ...prev,
      currency: {
        ...prev.currency,
        [field]: value
      }
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ color: '#ffffff', mb: 4, fontWeight: 'bold' }}>
          Settings
        </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Settings saved successfully!
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, rgb(30, 30, 30) 0%, rgb(35, 34, 38) 50%)',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CurrencyIcon sx={{ color: '#00ff88', mr: 2, fontSize: 28 }} />
                <Typography variant="h6" sx={{ color: '#ffffff' }}>
                  Currency & Financial Settings
        </Typography>
      </Box>
      
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#b0b0b0' }}>Display Currency</InputLabel>
                    <Select
                      value={settings.currency.display}
                      onChange={(e) => updateCurrencySetting('display', e.target.value)}
                      sx={{ color: '#ffffff' }}
                    >
                      {getCurrencyOptions().map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Chip 
                              label={option.symbol} 
                              size="small" 
                              sx={{ mr: 1, minWidth: 40 }} 
                            />
                            {option.value} - {option.name}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.currency.autoConvert}
                        onChange={(e) => updateCurrencySetting('autoConvert', e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#00ff88',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#00ff88',
                          },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ color: '#ffffff', mr: 1 }}>
                          Auto-convert amounts
        </Typography>
                        <Tooltip title="Automatically convert all amounts to your display currency">
                          <CurrencyIcon sx={{ color: '#b0b0b0', fontSize: 16 }} />
                        </Tooltip>
                      </Box>
                    }
                  />
                </Grid>
              </Grid>

              <Typography variant="body2" sx={{ color: '#b0b0b0', mt: 2 }}>
                Choose your preferred currency for displaying amounts throughout the application.
                Auto-convert will show all amounts in your selected currency using current exchange rates.
        </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleReset}
              disabled={loading}
              sx={{ 
                color: '#b0b0b0', 
                borderColor: '#b0b0b0',
                '&:hover': {
                  borderColor: '#ffffff',
                  color: '#ffffff'
                }
              }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={loading}
              sx={{ 
                bgcolor: '#00ff88', 
                color: '#000',
                '&:hover': {
                  bgcolor: '#00cc70'
                }
              }}
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>
      </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Settings; 